import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { PasswordField, useNotificationContext } from '@ospace/shared'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

import { AuthLayout } from '../components/AuthLayout'
import { UserService } from '../services/user'

const initialValues = {
  confirmationCode: '',
  password: '',
  confirmPassword: '',
}

const forgotPasswordSchema = Yup.object().shape({
  confirmationCode: Yup.number().required('Code is required'),
  password: Yup.string()
    .min(8, 'Minimum 8 letters')
    .max(50, 'Maximum 50 letters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .min(8, 'Minimum 8 letters')
    .max(50, 'Maximum 50 letters')
    .required('Confirm Password is required'),
})

export function ConfirmForgotPassword() {
  const { setNotification } = useNotificationContext()
  const navigate = useNavigate()
  const location: any = useLocation()
  const mutation = useMutation(
    (values: any) => {
      return UserService.confirmForgotPassword({
        email: location?.state?.email,
        confirmationCode: values.confirmationCode,
        password: values.password,
      })
    },
    {
      onSuccess: () => {
        setNotification({
          active: true,
          message: 'Your password has been sucessfully changed.',
          type: 'success',
        })
        navigate('/auth/login')
      },
      onError: (e: any) => {
        setNotification({
          active: true,
          message: e.response.data || e.message,
          type: 'danger',
        })
      },
    }
  )

  const onSubmit = async (values: any) => {
    await mutation.mutateAsync(values)
  }

  const validateConfirmPassword = (pass: any, value: any) => {
    let error = ''
    if (pass && value) {
      if (pass !== value) {
        error = 'Password not matched'
      }
    }
    return error
  }

  return (
    <AuthLayout>
      <Formik
        validationSchema={forgotPasswordSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ values }) => (
          <Form className='form w-100'>
            <div className='mb-lg-15 alert alert-success'>
              <div className='alert-text font-weight-bold'>
                If that email exists in our system, we will send you a password reset email. If you
                don’t receive this email, please contact your NEXTGEN account manager.
              </div>
            </div>

            <div className='fv-row mb-10'>
              <label className='d-flex align-items-center fs-6 fw-bolder text-gray-900 mb-2'>
                <span className='required'>Confirmation code</span>
              </label>

              <Field
                type='text'
                className='form-control form-control-lg form-control-solid'
                name='confirmationCode'
              />
              <div className='text-danger mt-2'>
                <ErrorMessage name='confirmationCode' />
              </div>
            </div>

            <div className='fv-row mb-10'>
              <label className='d-flex align-items-center fs-6 fw-bolder text-gray-900 mb-2'>
                <span className='required'>Password</span>
              </label>

              <Field
                type='password'
                component={PasswordField}
                className='form-control form-control-lg form-control-solid'
                name='password'
              />
              <div className='text-danger mt-2'>
                <ErrorMessage name='password' />
              </div>
            </div>

            <div className='fv-row mb-10'>
              <label className='d-flex align-items-center fs-6 fw-bolder text-gray-900 mb-2'>
                <span className='required'>Confirm Password</span>
              </label>

              <Field
                type='password'
                component={PasswordField}
                className='form-control form-control-lg form-control-solid'
                name='confirmPassword'
                validate={(value: any) => validateConfirmPassword(values.password, value)}
              />
              <div className='text-danger mt-2'>
                <ErrorMessage name='confirmPassword' />
              </div>
            </div>

            <div className='text-center'>
              <button
                type='submit'
                className='btn btn-lg btn-primary me-3'
                disabled={mutation.isLoading}
              >
                {mutation.isLoading && (
                  <span className='spinner-border spinner-border-sm align-middle mx-2' />
                )}
                <span className='indicator-label'>Submit</span>
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  )
}
